import React, { useMemo } from 'react'
import { ComposedChart, XAxis, YAxis, ResponsiveContainer, ReferenceArea, Line } from 'recharts'
import ToolTipAI360 from '../ai360-line-chart/ToolTipAI360'
import { AI360ChartData, AI360Mode, AI360ModeType } from 'src/Libs/ai-360/types'
import { EmsBatteryMode, EmsSgReadyMode } from 'src/Libs/dynamic-power-prices/types'
import { BATTERY_STATUS_BADGES, getSgReadyStatusBadges } from 'src/Libs/ai-360/consts'
import { useTheme } from '@mui/material'
import { TimeInMilliseconds } from 'src/Libs/types'
import { onMouseLeaveAI360Chart, onMouseMoveAI360Chart } from 'src/Libs/ai-360/utils'
import { useAI360RegisterChartInstance } from 'src/Libs/ai-360/hooks'

interface Segment {
  start: number
  end: number
  mode: EmsBatteryMode | EmsSgReadyMode
}

interface Props {
  chartData: AI360ChartData[]
  height?: number
  syncId?: string
  mode: AI360ModeType
}

/**
 * create segments for the timeline.
 * segments show the 15-minute duration of each mode with a different color.
 */
const buildSegments = (chartData: AI360ChartData[], mode: AI360ModeType): Segment[] => {
  if (chartData.length < 2) return []

  const segments: Segment[] = []
  const lastIndex = chartData.length - 1

  for (let i = 0; i < lastIndex; i++) {
    const current = chartData[i]
    const next = chartData[i + 1]

    if (current.time == null || next.time == null) continue

    if (mode === AI360Mode.EmsBatteryMode) {
      segments.push({
        start: current.time,
        end: next.time,
        mode: current.emsBatteryMode || EmsBatteryMode.NormalOperation,
      })
      continue
    }

    if (mode === AI360Mode.SgReadyMode) {
      segments.push({
        start: current.time,
        end: next.time,
        mode: current.emsSgReadyMode || EmsSgReadyMode.Normal,
      })
    }
  }

  return segments
}

/**
 * This component renders a timeline of battery or SG Ready modes as colored segments.
 */
const AI360ModesTimeline: React.FC<Props> = ({ chartData, height = 26, syncId = 'ai360-detail-chart', mode }) => {
  const theme = useTheme()
  const segments = useMemo(() => buildSegments(chartData, mode), [chartData, mode])

  const registerChartInstance = useAI360RegisterChartInstance()

  if (!chartData || chartData.length === 0) return null

  const lastIndex = chartData.length - 1

  const minTime = chartData[0].time
  const maxTime = chartData[lastIndex].time

  const hasDomain = minTime != null && maxTime != null

  const tooltipAI360 = ToolTipAI360({})

  const getFillColorForMode = (ai360Mode: AI360ModeType, modeValue: EmsBatteryMode | EmsSgReadyMode): string => {
    if (ai360Mode === AI360Mode.SgReadyMode) {
      return getSgReadyStatusBadges(theme).find(b => b.status === modeValue)?.color ?? theme.palette.primary.light
    }

    return BATTERY_STATUS_BADGES.find(b => b.status === modeValue)?.color ?? theme.palette.primary.light
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart
        ref={registerChartInstance}
        data={chartData}
        syncId={syncId}
        margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
        onMouseMove={state => onMouseMoveAI360Chart(state, chartData)}
        onMouseLeave={() => onMouseLeaveAI360Chart(chartData)}
      >
        <XAxis dataKey="time" type="number" domain={hasDomain ? [minTime, maxTime] : undefined} hide />

        <YAxis hide domain={[0, 1]} />

        {/* Invisible line to trigger onMouseMove. */}
        <Line type="monotone" dot={false} activeDot={false} hide />

        {segments.map((seg, i) => {
          const fillColor = getFillColorForMode(mode, seg.mode)
          const isLast = i === segments.length - 1
          /* extend into next segment slightly, except the last. (to remove gaps and overlap visually) */
          const overlap = isLast ? 0 : TimeInMilliseconds.thirtySecond

          return <ReferenceArea key={i} x1={seg.start} x2={seg.end + overlap} y1={0} y2={2} fill={fillColor} ifOverflow="extendDomain" />
        })}

        {tooltipAI360}
      </ComposedChart>
    </ResponsiveContainer>
  )
}

export default AI360ModesTimeline
